import { produce } from 'immer';

import { type EngineStateBase } from '@blaze-runtime/engine-data-access';
import {
  type CanvasElement,
  type CanvasElementId,
  type CanvasNamespaceId,
} from '@blaze-runtime/engine-domain';

import { hasSlots } from '../../canvas-elements/element-model/element-props-guards';

import { calculateManyNewElementIds } from './place-element';
import { getAllChildrenIds } from './relationship';

/**
 * If the element is moved from one namespace to another, the ids need to be recalculated and adjusted for the namespace
 * @param state element's state
 * @param elementId added element id
 * @returns state after updates
 */
// TODO: test
export function updateNamespacesAfterMove(
  state: EngineStateBase,
  elementId?: CanvasElementId,
): EngineStateBase {
  if (!elementId) {
    return state;
  }
  const movedElement = state.entities[elementId];
  const parentElementId = movedElement?.parentNode?.id;
  if (!parentElementId) {
    throw new Error('Could not identify parent');
  }
  const parentNamespaceId = state.entities[parentElementId]?.ns;
  const ownNamespaceId = movedElement?.ns;

  // If the namespace didn't change - leave everything unchanged
  if (!parentNamespaceId || parentNamespaceId === ownNamespaceId) {
    return state;
  }

  const elementsInNewNs = state.ids
    .map((id) => state.entities[id])
    .filter((element) => element?.ns === parentNamespaceId);
  const namesUsedInNs = elementsInNewNs.map((element) => element?.name);

  // Step 1 - gather element ids to update
  const elementIdsToUpdate: CanvasElementId[] = getAllChildrenIds(state.entities, movedElement.id);

  // Step 2 - Generate new ids for each element
  const newIds = calculateManyNewElementIds(parentNamespaceId, elementIdsToUpdate.length);
  const newIdLUT = elementIdsToUpdate.reduce(
    (acc, id, index) => {
      acc[id] = newIds[index];
      return acc;
    },
    {} as Record<CanvasElementId, CanvasElementId>,
  );

  // Step 3 - Update the state
  return produce(state, (draft) => {
    elementIdsToUpdate.forEach((id) => {
      const element = draft.entities[id];
      if (!element) {
        throw new Error('Element not found');
      }

      // Change the dictionary 'key'
      draft.entities[newIdLUT[id]] = element;
      delete draft.entities[id];

      // Update id and namespace
      element.id = newIdLUT[element.id];
      element.ns = parentNamespaceId;

      // Update colliding name
      if (namesUsedInNs.includes(element?.name)) {
        // TODO: add proper name? (findNameForElement)
        element.name = `${element.name} (from ${ownNamespaceId})`;
      }

      // Update parentNode if there is a substitute
      if (element.parentNode?.id && newIdLUT[element.parentNode.id]) {
        element.parentNode.id = newIdLUT[element.parentNode.id];
      }

      // Update children ids
      updateChildrenIdsMutably(element, newIdLUT);
    });

    // Update parent children ids (the moved element was placed into the slot with previous id)
    const parentElement = draft.entities[parentElementId];
    if (parentElement) {
      updateChildrenIdsMutably(parentElement, newIdLUT);
    }

    // Update the ids list
    draft.ids = (draft.ids as CanvasElementId[]).filter((id) => !elementIdsToUpdate.includes(id));
    draft.ids.push(...newIds);
  });
}

function updateChildrenIdsMutably(
  element: CanvasElement,
  newIdLUT: Record<CanvasElementId, CanvasElementId>,
): void {
  if (hasSlots(element)) {
    for (const slotId of element.slots.ids) {
      const slot = element.slots[slotId];
      if (slot) {
        slot.children = slot.children.map((childId) => newIdLUT[childId] || childId);
      }
    }
  }
}

/**
 * Sets the namespace for hosting elements like navContainer, navSidebar, navBottombar
 * @param expectedSlotId
 * @param namespaceId
 * @param element
 */
export function populateNamespaceForHostTypeMutably(
  expectedSlotId: string,
  namespaceId: CanvasNamespaceId,
  element?: CanvasElement,
): void {
  if (!hasSlots(element)) {
    return;
  }

  for (const slotId of element.slots.ids) {
    if (slotId === expectedSlotId && element.slots[slotId]?.hosting?.ns === undefined) {
      const slot = element.slots[slotId];
      if (!slot) {
        throw new Error('Could not find slot');
      }
      slot.hosting = { ns: namespaceId };
    }
  }
}
